import React from 'react';
import { Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { PlmsStatus, WorkbookMapping } from '@/types/admin';

interface PLMSWorkbookSectionProps {
  plmsEmail: string;
  setPlmsEmail: (email: string) => void;
  plmsWorkbookId: string;
  setPlmsWorkbookId: (id: string) => void;
  plmsStatus: PlmsStatus | null;
  workbookMappings: WorkbookMapping[];
  handleAssignWorkbook: () => void;
  handleRemoveWorkbook: (email: string) => void;
  serviceAccount: string;
}

const PLMSWorkbookSection: React.FC<PLMSWorkbookSectionProps> = ({
  plmsEmail,
  setPlmsEmail,
  plmsWorkbookId,
  setPlmsWorkbookId,
  plmsStatus,
  workbookMappings,
  handleAssignWorkbook,
  handleRemoveWorkbook,
  serviceAccount
}) => {
  return (
    <Card className="border-none shadow-none">

      <CardContent className="px-0">
        <Alert variant="default" className="mb-6 bg-muted/50">
          Remember to share the workbook with the service account email:{' '}
          <code className="bg-secondary px-1 py-0.5 rounded text-sm font-mono">
            {serviceAccount}
          </code>
        </Alert>

        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-4 mb-6">
          <Input
            placeholder="User Email"
            value={plmsEmail}
            onChange={(e) => setPlmsEmail(e.target.value)}
            type="email"
          />
          <Input
            placeholder="Workbook ID"
            value={plmsWorkbookId}
            onChange={(e) => setPlmsWorkbookId(e.target.value)}
          />
          <Button 
            onClick={handleAssignWorkbook}
            disabled={!plmsEmail || !plmsWorkbookId}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
          >
            Assign Workbook
          </Button>
        </div>

        {plmsStatus && (
          <Alert className={`mb-6 ${
            plmsStatus.type === 'success' 
              ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800' 
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
          }`}>
            {plmsStatus.message}
          </Alert>
        )}

        {workbookMappings.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Current Workbook Mappings</h3>
            <div className="space-y-3">
              {workbookMappings.map((mapping) => (
                <div
                  key={mapping.email}
                  className="flex items-center justify-between p-4 bg-card border rounded-md shadow-sm"
                >
                  <div>
                    <div className="font-medium">{mapping.email}</div>
                    <div className="text-sm text-muted-foreground">
                      Workbook ID: {mapping.workbookId}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveWorkbook(mapping.email)}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PLMSWorkbookSection;
